import { useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";

export type FontOption = {
  label: string;
  value: string;
};

export const DEFAULT_FONTS: FontOption[] = [
  { label: "Default", value: "" },

  // Latin
  { label: "Inter", value: "Inter, system-ui, sans-serif" },
  { label: "Segoe UI", value: "Segoe UI, system-ui, sans-serif" },
  { label: "Merriweather", value: "Merriweather, serif" },

  // Arabic
  {
    label: "Cairo (Arabic)",
    value: "Cairo, 'Noto Naskh Arabic', serif",
  },
  {
    label: "Amiri (Arabic)",
    value: "Amiri, 'Noto Naskh Arabic', serif",
  },
  {
    label: "IBM Plex Arabic",
    value: "'IBM Plex Arabic', Cairo, serif",
  },
  {
    label: "Noto Kufi Arabic",
    value: "'Noto Kufi Arabic', Cairo, sans-serif",
  },

  // Dhivehi
  {
    label: "Faruma (Dhivehi)",
    value: "Faruma, 'MV Faseyha', serif",
  },
];

type FontHandlerProps = {
  editor: Editor | null;
  fonts?: FontOption[];
  defaultFont?: string;
};

export function FontHandler({
  editor,
  fonts = DEFAULT_FONTS,
  defaultFont = "Inter, system-ui, sans-serif",
}: FontHandlerProps) {
  const [fontFamily, setFontFamily] = useState(defaultFont);

  // Apply default font once
  useEffect(() => {
    if (!editor) return;

    editor.chain().focus().setMark("textStyle", {
      fontFamily: defaultFont,
    }).run();
  }, [editor, defaultFont]);

  // Auto font by direction / language
  useEffect(() => {
    if (!editor) return;

    const dom = editor.view.dom;
    const dir = dom.getAttribute("dir");
    const lang = dom.getAttribute("lang");

    let autoFont = defaultFont;

    if (lang === "dv") {
      autoFont = "Faruma, 'MV Faseyha', serif";
    } else if (dir === "rtl") {
      autoFont = "Cairo, 'Noto Naskh Arabic', serif";
    }

    setFontFamily(autoFont);

    editor.chain().focus().setMark("textStyle", {
      fontFamily: autoFont,
    }).run();
  }, [editor, defaultFont]);

  const applyFont = (font: string) => {
    setFontFamily(font);
    if (!editor) return;

    editor
      .chain()
      .focus()
      .setMark("textStyle", font ? { fontFamily: font } : {})
      .run();
  };

  return (
    <select
      value={fontFamily}
      onChange={(e) => applyFont(e.target.value)}
      className="border rounded px-2 py-1 text-sm min-w-[160px]"
    >
      {fonts.map((font) => (
        <option key={font.value} value={font.value}>
          {font.label}
        </option>
      ))}
    </select>
  );
}